import { PillarsBetaLogo } from "@/components/pillars-beta-logo"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useToast } from "@/components/ui/use-toast"
import { loginWithApple, loginWithGoogle, sendLoginEmail } from "@/lib/firebase"
import { captureError, trackEvent } from "@/lib/log"
import { ArrowLeft, Mail } from "lucide-react"
import { useState } from "react"

type EmailStage = "hidden" | "input" | "sent"

export function Login() {
  const { toast } = useToast()
  const [emailStage, setEmailStage] = useState<EmailStage>("hidden")
  const [email, setEmail] = useState(
    import.meta.env.VITE_APP_ENV === "development" ? "huzaifah@thepillarsapp.com" : ""
  )
  const [submitting, setSubmitting] = useState(false)

  async function onSubmitEmail(e: React.FormEvent) {
    e.preventDefault()
    if (!email || submitting) return
    setSubmitting(true)
    trackEvent("attempted to log in", { provider: "email" })
    try {
      await sendLoginEmail(email)
      setEmailStage("sent")
    } catch (error) {
      captureError(error, { tags: { source: "sendLoginEmail" } })
      toast({
        title: "Couldn't send the email",
        description: "Please check the address and try again.",
        variant: "destructive",
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="bg-[#afb9c3] w-screen h-screen flex p-5 justify-center items-center bg-cover md:bg-contain bg-no-repeat"
      style={{
        backgroundImage: `url(https://cdn.prod.website-files.com/604d480efd2aff6558953436/65f50333ccd81bc76a406d62_Background.png)`,
      }}
    >
      <div className="flex flex-col gap-5 items-center mb-20">
        <PillarsBetaLogo white />
        <div className="flex gap-5">
          <div className="w-1 bg-white rounded-full"></div>
          <div className="flex flex-col gap-2 min-w-[300px]">
            {emailStage === "hidden" && (
              <>
                {/* <Button
                  onClick={() => { trackEvent("attempted to log in", { provider: "apple" }); loginWithApple() }}
                  className="flex items-center gap-2 bg-white hover:bg-gray-100 hover:translate-x-1 transition-all duration-300 py-6"
                  disabled={true}
                >
                  <img src="/apple-logo.svg" className="h-6 mb-[2px]" />
                  <div className="text-lg text-black">Continue with Apple</div>
                </Button> */}
                <Button
                  onClick={() => { trackEvent("attempted to log in", { provider: "google" }); loginWithGoogle() }}
                  className="flex items-center gap-2 bg-white hover:bg-gray-100 hover:translate-x-1 transition-all duration-300 py-6"
                >
                  <img src="/google-logo.svg" className="h-6" />
                  <div className="text-lg text-gray-500">Continue with Google</div>
                </Button>
                <Button
                  onClick={() => setEmailStage("input")}
                  className="flex items-center gap-2 bg-white hover:bg-gray-100 hover:translate-x-1 transition-all duration-300 py-6"
                >
                  <Mail className="h-6 w-6 text-gray-500" />
                  <div className="text-lg text-gray-500">Continue with email</div>
                </Button>
              </>
            )}

            {emailStage === "input" && (
              <>
                <Button
                  variant="ghost"
                  onClick={() => { setEmailStage("hidden"); setEmail("") }}
                  className="flex items-center gap-2 text-white hover:bg-white/10 hover:text-white self-start"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <form onSubmit={onSubmitEmail} className="flex flex-col gap-2">
                  <Input
                    type="email"
                    required
                    autoFocus
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-white py-6 text-lg text-center"
                  />
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex items-center gap-2 bg-white hover:bg-gray-100 transition-all duration-300 py-6"
                  >
                    <div className="text-lg text-gray-500">
                      {submitting ? "Sending…" : "Send login link"}
                    </div>
                  </Button>
                </form>
              </>
            )}

            {emailStage === "sent" && (
              <>
                <Button
                  variant="ghost"
                  onClick={() => { setEmailStage("hidden"); setEmail("") }}
                  className="flex items-center gap-2 text-white hover:bg-white/10 hover:text-white self-start"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="bg-white rounded-md p-4 flex flex-col gap-2">
                  <div className="text-sm text-gray-700">
                    Check your inbox at <span className="font-medium">{email}</span> for a sign-in link.
                  </div>
                  <button
                    type="button"
                    onClick={() => { setEmailStage("input") }}
                    className="text-xs text-gray-500 underline self-start"
                  >
                    Use a different email
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
